import { Controller } from "@hotwired/stimulus";

const STORAGE_KEY_PREFIX = "docs_api_token";
const TOKEN_PLACEHOLDER = "YOUR_API_TOKEN";

export default class extends Controller {
  static targets = ["dialog", "input", "status", "createButton"];
  static values = { accountSlug: String };

  get storageKey() {
    const slug = this.accountSlugValue;
    return slug ? `${STORAGE_KEY_PREFIX}_${slug}` : STORAGE_KEY_PREFIX;
  }

  connect() {
    this.scanAndReplace();

    // Re-scan when turbo frames load new content
    this.element.addEventListener("turbo:frame-load", this.scanAndReplace);
  }

  disconnect() {
    this.element.removeEventListener("turbo:frame-load", this.scanAndReplace);
  }

  scanAndReplace = () => {
    this.addCopyButtons();
    this.wrapTokenPlaceholders();
    this.applyStoredToken();
  };

  addCopyButtons() {
    const preBlocks = this.element.querySelectorAll(
      "pre:not(docs-code-block pre)",
    );
    preBlocks.forEach((pre) => {
      if (pre.parentElement?.classList.contains("docs-pre-wrapper")) return;
      const wrapper = document.createElement("div");
      wrapper.className = "docs-pre-wrapper";
      pre.parentNode.insertBefore(wrapper, pre);
      wrapper.appendChild(pre);
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "docs-copy-btn";
      btn.title = "Copy to clipboard";
      btn.setAttribute("data-action", "click->docs-api-token#copyCode");
      btn.innerHTML =
        '<svg viewBox="0 0 16 16" fill="currentColor"><path d="M0 6.75C0 5.784.784 5 1.75 5h1.5a.75.75 0 0 1 0 1.5h-1.5a.25.25 0 0 0-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 0 0 .25-.25v-1.5a.75.75 0 0 1 1.5 0v1.5A1.75 1.75 0 0 1 9.25 16h-7.5A1.75 1.75 0 0 1 0 14.25Z"/><path d="M5 1.75C5 .784 5.784 0 6.75 0h7.5C15.216 0 16 .784 16 1.75v7.5A1.75 1.75 0 0 1 14.25 11h-7.5A1.75 1.75 0 0 1 5 9.25Zm1.75-.25a.25.25 0 0 0-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 0 0 .25-.25v-7.5a.25.25 0 0 0-.25-.25Z"/></svg>';
      wrapper.appendChild(btn);
    });
  }

  wrapTokenPlaceholders() {
    const codeBlocks = this.element.querySelectorAll(
      "docs-code-block code, pre code",
    );
    codeBlocks.forEach((code) => {
      // Skip if already processed
      if (code.querySelector(".docs-api-token-clickable")) return;

      const walker = document.createTreeWalker(
        code,
        NodeFilter.SHOW_TEXT,
        null,
      );
      const nodes = [];
      let node;
      while ((node = walker.nextNode())) {
        if (node.textContent.includes(TOKEN_PLACEHOLDER)) {
          nodes.push(node);
        }
      }
      nodes.forEach((textNode) => {
        const parts = textNode.textContent.split(TOKEN_PLACEHOLDER);
        const fragment = document.createDocumentFragment();
        parts.forEach((part, i) => {
          if (i > 0) {
            const span = document.createElement("span");
            span.className = "docs-api-token-clickable";
            span.textContent = TOKEN_PLACEHOLDER;
            span.setAttribute("data-action", "click->docs-api-token#openModal");
            fragment.appendChild(span);
          }
          if (part) {
            fragment.appendChild(document.createTextNode(part));
          }
        });
        textNode.parentNode.replaceChild(fragment, textNode);
      });
    });
  }

  applyStoredToken() {
    const stored = localStorage.getItem(this.storageKey);
    if (!stored) return;

    const spans = this.element.querySelectorAll(".docs-api-token-clickable");
    spans.forEach((span) => {
      span.textContent = stored;
      span.classList.add("docs-api-token-applied");
    });
  }

  restorePlaceholders() {
    const spans = this.element.querySelectorAll(".docs-api-token-clickable");
    spans.forEach((span) => {
      span.textContent = TOKEN_PLACEHOLDER;
      span.classList.remove("docs-api-token-applied");
    });
  }

  openModal() {
    if (!this.hasDialogTarget) return;
    const stored = localStorage.getItem(this.storageKey);
    if (stored) {
      this.inputTarget.value = stored;
    }
    this.statusTarget.textContent = "";
    this.dialogTarget.showModal();
  }

  closeModal() {
    if (!this.hasDialogTarget) return;
    this.dialogTarget.close();
  }

  applyToken() {
    const value = this.inputTarget.value.trim();
    if (!value) {
      this.statusTarget.textContent = "Please enter an API key.";
      return;
    }

    this.restorePlaceholders();
    localStorage.setItem(this.storageKey, value);
    this.applyStoredToken();
    this.closeModal();
  }

  clearToken() {
    this.restorePlaceholders();
    localStorage.removeItem(this.storageKey);
    this.inputTarget.value = "";
    this.statusTarget.textContent = "Token cleared.";
  }

  copyCode(event) {
    const block =
      event.currentTarget.closest("docs-code-block") ||
      event.currentTarget.closest(".docs-pre-wrapper") ||
      event.currentTarget.closest("pre");
    if (!block) return;

    const code = block.querySelector("code") || block;

    const text = code.textContent;
    const btn = event.currentTarget;

    navigator.clipboard
      .writeText(text)
      .then(() => {
        btn.classList.add("docs-copy-btn--copied");
        clearTimeout(btn._copyTimeout);
        btn._copyTimeout = setTimeout(() => {
          btn.classList.remove("docs-copy-btn--copied");
        }, 1500);
      })
      .catch(() => {});
  }

  async createKey() {
    if (!this.hasCreateButtonTarget) return;

    this.createButtonTarget.disabled = true;
    this.createButtonTarget.textContent = "Creating\u2026";
    this.statusTarget.textContent = "";

    try {
      const response = await fetch(
        this.createButtonTarget.dataset.createUrl || "/api/create-token",
        {
          method: "POST",
          headers: {
            "X-CSRF-Token": document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute("content"),
            Accept: "application/json",
          },
        },
      );

      if (!response.ok) {
        const data = await response.json();
        this.statusTarget.textContent = data.error || "Failed to create key.";
        return;
      }

      const data = await response.json();
      this.inputTarget.value = data.key;
      this.statusTarget.textContent =
        "Temporary key created (expires in 1 hour). Click \u2018Use this key\u2019 to apply it.";
    } catch {
      this.statusTarget.textContent = "Network error. Please try again.";
    } finally {
      this.createButtonTarget.disabled = false;
      this.createButtonTarget.textContent = "Create temporary API key";
    }
  }
}
